/**
 * HTML sanitization for template editor (local copy so template folder resolves at build time).
 * Uses DOMPurify; preserves merge tokens. See also client/src/utils/sanitizeHtml.js.
 */

import DOMPurify from 'dompurify';

const ALLOWED_TAGS = [
  'p', 'div', 'span', 'strong', 'em', 'u', 's', 'strike',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li', 'br', 'hr',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th',
  'img', 'a', 'blockquote', 'pre', 'code',
];

const ALLOWED_ATTR = [
  'href', 'src', 'alt', 'title', 'width', 'height',
  'class', 'data-key', 'data-token', 'target', 'rel',
  'style', 'align', 'colspan', 'rowspan',
];

const DANGEROUS_STYLE_PATTERN = /(expression|javascript|vbscript|url\s*\(\s*['"]?\s*javascript|behavior\s*:)/i;

function sanitizeStyleValue(value) {
  if (typeof value !== 'string') return '';
  return value.replace(DANGEROUS_STYLE_PATTERN, '').trim();
}

function sanitizeStyleAttribute(styleStr) {
  if (!styleStr || typeof styleStr !== 'string') return '';
  const parts = styleStr.split(';').map((part) => {
    const idx = part.indexOf(':');
    if (idx === -1) return '';
    const prop = part.slice(0, idx).trim().toLowerCase();
    const val = part.slice(idx + 1).trim();
    if (/^(expression|javascript|vbscript|behavior)/i.test(prop)) return '';
    return `${prop}: ${sanitizeStyleValue(val)}`;
  }).filter(Boolean);
  return parts.join('; ');
}

let hookAdded = false;

function createSanitizeConfig() {
  if (!hookAdded && typeof DOMPurify.addHook === 'function') {
    DOMPurify.addHook('afterSanitizeAttributes', (node) => {
      if (node.hasAttribute?.('style')) {
        const style = node.getAttribute('style');
        node.setAttribute('style', sanitizeStyleAttribute(style));
      }
    });
    hookAdded = true;
  }
  return {
    ALLOWED_TAGS,
    ALLOWED_ATTR: [...ALLOWED_ATTR],
    ADD_ATTR: ['target'],
    ADD_TAGS: [],
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'formaction'],
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
  };
}

let sanitizeConfig = null;

function getConfig() {
  if (!sanitizeConfig) sanitizeConfig = createSanitizeConfig();
  return sanitizeConfig;
}

export function sanitizeHtml(dirty) {
  if (dirty == null || typeof dirty !== 'string') return '';
  const clean = DOMPurify.sanitize(dirty, getConfig());
  return clean;
}

export default sanitizeHtml;
